import { useCallback, useEffect, useRef, useState } from "react";

const POLL_MS    = 30_000;
const TIMEOUT_MS = 10_000;

/**
 * useVelocityManifest — per-site velocity manifest polling.
 *
 * Activates when `selectedProduct === "velocity"` and an activeSite is known.
 * Tears down (clears manifest) when any other product is selected.
 *
 * @param {string}      selectedProduct  Current radar product id.
 * @param {string|null} activeSite       ICAO site code (null = backend default).
 * @param {string}      apiBase          API root URL.
 * @returns {{ manifest, error, loading, retry }}
 */
export default function useVelocityManifest(selectedProduct, activeSite, apiBase) {
  const [manifest, setManifest] = useState(null);
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(false);

  const abortRef  = useRef(null);
  const timerRef  = useRef(null);
  const reqRef    = useRef(0);
  const fetchRef  = useRef(null);

  useEffect(() => {
    if (selectedProduct !== "velocity") {
      abortRef.current?.abort();
      clearInterval(timerRef.current);
      setManifest(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const siteParam = activeSite ? `&site=${activeSite}` : "";

    const fetchVel = async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      reqRef.current  += 1;
      const reqId = reqRef.current;
      const timeout = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
      try {
        const res = await fetch(
          `${apiBase}/radar/manifest?product=velocity${siteParam}`,
          { signal: ctrl.signal, cache: "no-store" },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (reqId !== reqRef.current) return;
        setManifest(data);
        setError(null);
      } catch (err) {
        if (reqId !== reqRef.current) return;
        if (err.name === "AbortError") {
          setError("Request timed out");
        } else {
          setError(err.message || "Failed to load velocity data");
        }
      } finally {
        clearTimeout(timeout);
        if (reqId === reqRef.current) setLoading(false);
      }
    };

    fetchRef.current = fetchVel;
    fetchVel();
    timerRef.current = setInterval(fetchVel, POLL_MS);
    return () => { clearInterval(timerRef.current); abortRef.current?.abort(); };
  }, [selectedProduct, activeSite, apiBase]);

  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    fetchRef.current?.();
  }, []);

  return { manifest, error, loading, retry };
}
